// Helpers for asking the browser to keep our `localStorage` cache
// from being evicted under disk pressure.
//
// Why this lives in its own module
// -------------------------------
// `navigator.storage.persist()` is the only API that lets a page opt
// in to durable storage. Modern browsers (Safari, Chromium mobile,
// Firefox private-browsing) will silently evict non-essential
// `localStorage` once the device is under pressure; once evicted,
// the chat cache is gone and the user sees an empty thread on next
// load. Calling `.persist()` asks the browser to keep the origin's
// storage across the "best-effort" boundary. Browsers may still
// prompt the user, but they NEVER prompt more than once per origin
// per session — so calling it once is enough.
//
// Throttle strategy
// -----------------
// We gate the actual call behind a `sessionStorage` flag so we
// don't repeat the round-trip on every navigation. A tab re-load
// counts as a new session, so the first chat mount of a tab always
// re-issues; the rest of the session is silent. `sessionStorage` is
// intentionally NOT `localStorage` here — we want each tab to make
// the ask once, never carry the decision across browser restarts.
//
// Errors are swallowed. Storage eviction is best-effort infrastructure;
// a failed request must never break the chat surface.
const SESSION_FLAG = "famchest:storage:persist:requested";

// Keep concurrent callers on the same request. The session flag prevents
// later calls from re-issuing the browser prompt, while this in-flight
// promise lets callers that arrive before the first response receive the
// same result instead of observing the flag and returning a false negative.
let persistInFlight: Promise<boolean> | null = null;

/**
 * Ask the browser to keep this origin's storage from being evicted
 * under disk pressure. Safe to call repeatedly — only the first call
 * per session actually talks to the browser; subsequent calls are a
 * no-op. Resolves to `true` if the browser reports the storage is
 * (or already was) persistent, `false` otherwise (denied, not
 * supported, or errored). Never throws.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const storage = window.navigator?.storage;
  if (!storage || typeof storage.persist !== "function") return false;

  // Session gate: the browser dedupes the prompt, but we still avoid
  // the round-trip and the (small) chance of a microtask flicker.
  if (window.sessionStorage?.getItem(SESSION_FLAG) === "1") {
    if (persistInFlight) return persistInFlight;
    // Already asked this session — peek at the persistence state to
    // honour callers that asked for the boolean outcome.
    if (typeof storage.persisted === "function") {
      try {
        return await storage.persisted();
      } catch {
        return false;
      }
    }
    return false;
  }

  try {
    // Mark first so a race between two concurrent callers (e.g. two
    // chat mounts in the same tab) still issues the request once.
    window.sessionStorage?.setItem(SESSION_FLAG, "1");
    const request = (async () => {
      try {
        return await storage.persist!();
      } catch {
        // The spec's promise rejection is reserved for cases where
        // invoking persist itself is invalid in the current context.
        return false;
      }
    })();
    persistInFlight = request;
    void request.then(() => {
      if (persistInFlight === request) persistInFlight = null;
    });
    return await request;
  } catch {
    // Session storage can also be unavailable in a restricted context.
    persistInFlight = null;
    return false;
  }
}

/**
 * Probe whether the origin's storage is currently persistent. Useful
 * for diagnostics. Resolves to `false` when the API is unavailable.
 */
export async function isStoragePersistent(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const storage = window.navigator?.storage;
  if (!storage || typeof storage.persisted !== "function") return false;
  try {
    return await storage.persisted();
  } catch {
    return false;
  }
}
